// src/helpers/handlebars_validation.js
import { white_list_outgoing_person_fields } from './static_vars';

/**
 * Checks if a value contains unparsed handlebars syntax
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value contains unparsed handlebars
 */
export function contains_unparsed_handlebars(value) {
	if (typeof value !== 'string') {
		return false;
	}

	try {
		const handlebars_pattern = /\{\{[a-zA-Z_][a-zA-Z0-9_-]*\}\}/;
		return handlebars_pattern.test(value);
	} catch (error) {
		window?.data_hub?.error_function?.(`Error checking for unparsed handlebars: ${error}`, null);
		return false;
	}
}

/**
 * Validates user data object and removes any fields with unparsed handlebars
 * @param {Object} user_data - The user data object to validate
 * @param {Array} whitelisted_fields - Array of whitelisted field names to check
 * @param {Function} error_function - Optional error function to log issues (internal_msg, user_facing_msg)
 * @returns {Object} - Cleaned user data object with handlebars fields removed
 */
export function validate_and_clean_handlebars_from_user_data(
	user_data = {},
	whitelisted_fields = white_list_outgoing_person_fields,
	error_function = window?.data_hub?.error_function
) {
	if (!user_data || typeof user_data !== 'object') {
		return user_data;
	}

	if (!Array.isArray(whitelisted_fields) || whitelisted_fields.length === 0) {
		error_function?.('whitelisted_fields is not a valid array or is empty, using default', null);
		whitelisted_fields = white_list_outgoing_person_fields;
	}

	const cleaned_user_data = {};

	try {
		Object.keys(user_data).forEach(key => {
			const value = user_data[key];

			// Strip unparsed Handlebars from EVERY key, not just whitelisted ones.
			// Whitelisting is a separate concern (which fields belong to a "person"); sanitization
			// must apply universally so attacker-controlled URL params with `{{token}}` strings
			// can't reach the server template-resolution layer via non-whitelisted keys.
			if (contains_unparsed_handlebars(value)) {
				error_function?.(`Removed unparsed handlebars from field '${key}': ${value}`, null);
				return;
			}
			cleaned_user_data[key] = value;
		});
	} catch (error) {
		error_function?.(`Error validating user data for handlebars: ${error}`, null);
		return user_data;
	}

	return cleaned_user_data;
}

/**
 * Validates a single field value for unparsed handlebars
 * @param {string} key - The field key
 * @param {any} value - The field value
 * @param {Array} whitelisted_fields - Array of whitelisted field names to check
 * @param {Function} error_function - Optional error function to log issues (internal_msg, user_facing_msg)
 * @returns {any} - The value if valid, null if contains handlebars
 */
export function validate_single_field_for_handlebars(
	key,
	value,
	whitelisted_fields = white_list_outgoing_person_fields,
	error_function = window?.data_hub?.error_function
) {
	if (!Array.isArray(whitelisted_fields) || whitelisted_fields.length === 0) {
		error_function?.('whitelisted_fields is not a valid array or is empty, using default', null);
		whitelisted_fields = white_list_outgoing_person_fields;
	}

	// Apply handlebars sanitization to every key, not just whitelisted ones.
	// Whitelisting is a separate concern from sanitization.
	try {
		if (contains_unparsed_handlebars(value)) {
			error_function?.(`Skipping field '${key}' with unparsed handlebars: ${value}`, null);
			return null;
		}
	} catch (error) {
		error_function?.(`Error validating field '${key}' for handlebars: ${error}`, null);
		return value;
	}

	return value;
}
